import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Receipt, CreditCard, Calendar, LogOut, Plus, DollarSign, Home, RefreshCw, Edit2, X, Upload, Trash2, Image as ImageIcon, Clock } from 'lucide-react';
import axios from 'axios';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import TwoStepTransactionForm from './components/TwoStepTransactionForm';
import ImageZoom from './components/ImageZoom';
import TimeAuditTab from './components/TimeAuditTab';

// Format a transaction amount for display. Returns (refunds) are stored as
// negative amounts and render as "-$6.47" instead of "$-6.47".
const formatAmount = (amount) => {
  const value = parseFloat(amount) || 0;
  return `${value < 0 ? '-' : ''}$${Math.abs(value).toFixed(2)}`;
};

// Pull-to-refresh component
const PullToRefresh = ({ onRefresh, isRefreshing, children }) => {
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);

  const threshold = 80; // Minimum pull distance to trigger refresh

  const handleTouchStart = (e) => {
    // Only activate pull-to-refresh if we're at the top of the container
    const container = e.currentTarget;
    if (container.scrollTop === 0) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling || isRefreshing) return;

    const container = e.currentTarget;
    if (container.scrollTop > 0) {
      setIsPulling(false);
      setPullDistance(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, (currentY - startY) * 0.4); // Damping factor

    if (distance > 0) {
      e.preventDefault(); // Prevent default scroll behavior
      setPullDistance(Math.min(distance, threshold * 1.5));
      setCurrentY(currentY);
    }
  };

  const handleTouchEnd = () => {
    if (!isPulling) return;

    if (pullDistance >= threshold && !isRefreshing) {
      onRefresh();
    }

    setIsPulling(false);
    setPullDistance(0);
    setStartY(0);
    setCurrentY(0);
  };

  return (
    <div
      className="relative overflow-auto h-full"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className={`absolute top-0 left-0 right-0 flex items-center justify-center transition-transform duration-200 ${
          pullDistance > 0 || isRefreshing ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{
          transform: `translateY(${pullDistance > 0 ? pullDistance - 60 : isRefreshing ? 0 : -60}px)`,
          height: '60px'
        }}
      >
        <div className="flex items-center space-x-2 text-primary-600">
          <RefreshCw
            className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span className="text-sm font-medium">
            {isRefreshing
              ? 'Refreshing...'
              : pullDistance >= threshold
                ? 'Release to refresh'
                : 'Pull to refresh'
            }
          </span>
        </div>
      </div>

      {/* Content */}
      <div
        className="transition-transform duration-200"
        style={{
          transform: `translateY(${pullDistance > 0 ? pullDistance : isRefreshing ? 60 : 0}px)`
        }}
      >
        {children}
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [unsubmittedTransactions, setUnsubmittedTransactions] = useState([]);
  const [userTransactions, setUserTransactions] = useState([]);
  const [isSubmittingTransaction, setIsSubmittingTransaction] = useState(false);
  const [transactionMessage, setTransactionMessage] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [prefillData, setPrefillData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Time Audit pending violations count
  const [pendingViolationsCount, setPendingViolationsCount] = useState(0);

  // Format date without timezone conversion
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date string and format manually to avoid any timezone issues
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUnsubmittedTransactions();
      fetchUserTransactions();
      // Fetch pending violations count if user has time audit access
      if (user.role === 'admin' || user.appAccess?.timeAudit) {
        fetchPendingViolationsCount();
      }
    }
  }, [user]);

  // Auto-refresh when app comes into focus (user returns to PWA)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh data when user returns to the app
        fetchUnsubmittedTransactions();
        fetchUserTransactions();
        if (user.role === 'admin' || user.appAccess?.timeAudit) {
          fetchPendingViolationsCount();
        }
      }
    };

    const handleFocus = () => {
      // Refresh data when window gains focus
      fetchUnsubmittedTransactions();
      fetchUserTransactions();
      if (user.role === 'admin' || user.appAccess?.timeAudit) {
        fetchPendingViolationsCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  // Refresh data when switching tabs
  useEffect(() => {
    if (user && activeTab) {
      fetchUnsubmittedTransactions();
      fetchUserTransactions();
      if (user.role === 'admin' || user.appAccess?.timeAudit) {
        fetchPendingViolationsCount();
      }
    }
  }, [activeTab]);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    const token = localStorage.getItem('token');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setUnsubmittedTransactions([]);
    setUserTransactions([]);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }


  const fetchUnsubmittedTransactions = async () => {
    try {
      const response = await axios.get('/api/user-transactions/unmatched-statements');
      setUnsubmittedTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching unmatched statement transactions:', error);
    }
  };

  const fetchPendingViolationsCount = async () => {
    try {
      const response = await axios.get('/api/time-audit/violations?activeOnly=true&status=pending&limit=500');
      const violations = response.data.violations || [];
      setPendingViolationsCount(violations.length);
    } catch (error) {
      console.error('Error fetching pending violations count:', error);
    }
  };

  const fetchUserTransactions = async (reset = true) => {
    try {
      const page = reset ? 1 : currentPage;
      const response = await axios.get(`/api/user-transactions?page=${page}&limit=20`);

      if (reset) {
        setUserTransactions(response.data.transactions || []);
        setCurrentPage(1);
      } else {
        setUserTransactions(prev => [...prev, ...(response.data.transactions || [])]);
      }

      // Check if there are more transactions
      const { page: responsePage, totalPages } = response.data.pagination;
      setHasMoreTransactions(responsePage < totalPages);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
    }
  };

  const loadMoreTransactions = async () => {
    if (isLoadingMore || !hasMoreTransactions) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await axios.get(`/api/user-transactions?page=${nextPage}&limit=20`);

      setUserTransactions(prev => [...prev, ...(response.data.transactions || [])]);
      setCurrentPage(nextPage);

      // Check if there are more transactions
      const { page: responsePage, totalPages } = response.data.pagination;
      setHasMoreTransactions(responsePage < totalPages);
    } catch (error) {
      console.error('Error loading more transactions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      // Reset pagination state and refresh both data sources
      setCurrentPage(1);
      setHasMoreTransactions(true);
      await Promise.all([
        fetchUnsubmittedTransactions(),
        fetchUserTransactions(true) // true to reset to page 1
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      // Add a small delay to show the refresh indicator
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };


  const handleUnmatchedTransactionClick = (transaction) => {
    // Extract vendor name from description (clean up common patterns)
    let vendor = transaction.description;
    
    // Clean up common credit card transaction patterns
    vendor = vendor.replace(/\*[A-Z0-9]+$/, ''); // Remove reference numbers like *G76E61443
    vendor = vendor.replace(/^([A-Z]+\s+MKTPL|AMAZON\.COM|AMAZON)\*?/i, 'Amazon'); // Clean Amazon patterns
    vendor = vendor.trim();
    
    // Set prefill data
    setPrefillData({
      transactionDate: transaction.transactionDate,
      vendor: vendor,
      amount: Math.abs(parseFloat(transaction.amount)).toString() // Convert negative amounts to positive
    });
    
    // Navigate to home tab
    setActiveTab('home');
  };

  const handleTransactionSubmit = async (transactionData) => {
    setIsSubmittingTransaction(true);
    setTransactionMessage('');

    try {
      // Create FormData if there are images, otherwise send JSON
      if (transactionData.receiptImages && transactionData.receiptImages.length > 0) {
        console.log('Preparing to submit transaction with', transactionData.receiptImages.length, 'images');

        // Log image details
        let totalSize = 0;
        transactionData.receiptImages.forEach((image, index) => {
          console.log(`Image ${index + 1}: ${image.name}, Size: ${(image.size / 1024).toFixed(2)} KB, Type: ${image.type}`);
          totalSize += image.size;
        });
        console.log(`Total upload size: ${(totalSize / 1024).toFixed(2)} KB (${(totalSize / 1024 / 1024).toFixed(2)} MB)`);

        const formData = new FormData();
        formData.append('transactionDate', transactionData.transactionDate);
        formData.append('amount', transactionData.amount);
        formData.append('vendor', transactionData.vendor);
        if (transactionData.category) formData.append('category', transactionData.category);
        if (transactionData.notes) formData.append('notes', transactionData.notes);
        if (transactionData.creditCardId) formData.append('creditCardId', transactionData.creditCardId);

        // Append all images
        transactionData.receiptImages.forEach((image, index) => {
          formData.append('receiptImages', image);
          console.log(`Appended image ${index + 1} to FormData`);
        });

        console.log('Sending transaction request to backend...');

        await axios.post('/api/user-transactions', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 60000, // 60 second timeout
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
          },
        });

        console.log('Transaction submitted successfully');
      } else {
        // Remove receiptImages from data if it's null/empty
        const { receiptImages, receiptImage, ...dataToSend } = transactionData;
        await axios.post('/api/user-transactions', dataToSend);
      }

      setTransactionMessage('Transaction added successfully!');
      fetchUserTransactions();
      fetchUnsubmittedTransactions(); // Refresh unmatched transactions
      setPrefillData(null); // Clear prefill data after successful submission
      // Clear the message after 3 seconds
      setTimeout(() => setTransactionMessage(''), 3000);
    } catch (error) {
      console.error('========== TRANSACTION SUBMISSION ERROR ==========');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Full error:', error);
      console.error('================================================');

      // Display specific error message based on error type
      let errorMessage = 'Error adding transaction. Please try again.';

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timeout. The images may be too large. Please try with fewer or smaller images.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Images are too large. Please use smaller images or fewer files.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setTransactionMessage(errorMessage);
      throw error; // Re-throw so the form doesn't clear on error
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  const handleEditTransaction = (transaction) => {
    // Check if transaction is matched to a processed group
    if (transaction.statementTransaction?.statementGroup?.processed) {
      setTransactionMessage('Cannot edit: This transaction is matched to a processed statement group.');
      setTimeout(() => setTransactionMessage(''), 5000);
      return;
    }

    setEditingTransaction(transaction);
    setShowEditModal(true);
  };

  const handleUpdateTransaction = async (transactionData) => {
    if (!editingTransaction) return;

    setIsSubmittingTransaction(true);
    setTransactionMessage('');

    try {
      // Create FormData if there's an image or removeImage flag, otherwise send JSON
      if (transactionData.receiptImage || transactionData.removeImage) {
        const formData = new FormData();
        formData.append('transactionDate', transactionData.transactionDate);
        formData.append('amount', transactionData.amount);
        formData.append('vendor', transactionData.vendor);
        if (transactionData.category) formData.append('category', transactionData.category);
        if (transactionData.notes) formData.append('notes', transactionData.notes);
        if (transactionData.creditCardId) formData.append('creditCardId', transactionData.creditCardId);
        if (transactionData.removeImage) formData.append('removeImage', 'true');
        if (transactionData.receiptImage) formData.append('receiptImage', transactionData.receiptImage);

        await axios.put(`/api/user-transactions/${editingTransaction.id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        const { receiptImage, removeImage, ...dataToSend } = transactionData;
        await axios.put(`/api/user-transactions/${editingTransaction.id}`, dataToSend);
      }

      setTransactionMessage('Transaction updated successfully!');
      setShowEditModal(false);
      setEditingTransaction(null);
      fetchUserTransactions();
      fetchUnsubmittedTransactions();
      setTimeout(() => setTransactionMessage(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error updating transaction. Please try again.';
      setTransactionMessage(errorMessage);
      console.error('Transaction update error:', error);
      setTimeout(() => setTransactionMessage(''), 5000);
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }

    setIsSubmittingTransaction(true);
    setTransactionMessage('');

    try {
      await axios.delete(`/api/user-transactions/${transactionId}`);

      setTransactionMessage('Transaction deleted successfully!');
      setShowEditModal(false);
      setEditingTransaction(null);
      fetchUserTransactions();
      fetchUnsubmittedTransactions();
      setTimeout(() => setTransactionMessage(''), 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error deleting transaction. Please try again.';
      setTransactionMessage(errorMessage);
      console.error('Transaction delete error:', error);
      setTimeout(() => setTransactionMessage(''), 5000);
    } finally {
      setIsSubmittingTransaction(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingTransaction(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Receipt className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Receipt Manager</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user.firstName}!
              </span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-24">
        {activeTab === 'home' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4">
                <TwoStepTransactionForm
                  onSubmit={handleTransactionSubmit}
                  isSubmitting={isSubmittingTransaction}
                  prefillData={prefillData}
                  onPrefillDataUsed={() => setPrefillData(null)}
                />
                {transactionMessage && (
                  <div className={`mt-4 p-3 rounded-md text-sm ${transactionMessage.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {transactionMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recent' && (
          <HistoryTab
            userTransactions={userTransactions}
            hasMoreTransactions={hasMoreTransactions}
            isLoadingMore={isLoadingMore}
            loadMoreTransactions={loadMoreTransactions}
            formatDate={formatDate}
            handleEditTransaction={handleEditTransaction}
            handleRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        )}

        {activeTab === 'unmatched' && (
          <PullToRefresh onRefresh={handleRefresh} isRefreshing={isRefreshing}>
            <div className="space-y-8">
              {/* Unmatched Transactions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-5 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Missing Receipts</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Credit card transactions without matching receipts
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {unsubmittedTransactions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">All caught up!</h3>
                    <p className="mt-1 text-sm text-gray-500">No missing receipts.</p>
                  </div>
                ) : (
                  unsubmittedTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleUnmatchedTransactionClick(transaction)}
                    >
                      <div className="flex items-center">
                        <CreditCard className="h-8 w-8 text-gray-400" />
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-gray-900">
                            {transaction.description}
                          </h3>
                          <p className="text-sm text-gray-500">
                            ${Math.abs(parseFloat(transaction.amount)).toFixed(2)} • Card ending {transaction.cardNumber?.slice(-4) || transaction.cardNumber}
                          </p>
                          {transaction.category && (
                            <p className="text-xs text-gray-400 mt-1">{transaction.category}</p>
                          )}
                          <p className="text-xs text-primary-600 mt-1 font-medium">
                            Click to submit receipt →
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-500">
                          {formatDate(transaction.transactionDate)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            </div>
          </PullToRefresh>
        )}

        {activeTab === 'timeaudit' && (user.role === 'admin' || user.appAccess?.timeAudit) && (
          <TimeAuditTab
            user={user}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            onViolationUpdate={fetchPendingViolationsCount}
          />
        )}
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 safe-area-pb">
        <div className="flex justify-around items-center max-w-md mx-auto">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center p-2 rounded-lg ${
              activeTab === 'home'
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Home className="h-6 w-6" />
            <span className="text-xs mt-1">Home</span>
          </button>

          <button
            onClick={() => setActiveTab('recent')}
            className={`flex flex-col items-center p-2 rounded-lg ${
              activeTab === 'recent'
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <DollarSign className="h-6 w-6" />
            <span className="text-xs mt-1">History</span>
          </button>

          <button
            onClick={() => setActiveTab('unmatched')}
            className={`flex flex-col items-center p-2 rounded-lg relative ${
              activeTab === 'unmatched'
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="relative">
              <CreditCard className="h-6 w-6" />
              {unsubmittedTransactions.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {unsubmittedTransactions.length > 99 ? '99+' : unsubmittedTransactions.length}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">Missing Receipts</span>
          </button>

          {/* Time Audit Tab - Only show for users with access */}
          {(user.role === 'admin' || user.appAccess?.timeAudit) && (
            <button
              onClick={() => setActiveTab('timeaudit')}
              className={`flex flex-col items-center p-2 rounded-lg ${
                activeTab === 'timeaudit'
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="relative">
                <Clock className="h-6 w-6" />
                {pendingViolationsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {pendingViolationsCount > 99 ? '99+' : pendingViolationsCount}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1">Time Audit</span>
            </button>
          )}
        </div>
      </div>

      {/* Edit Transaction Modal */}
      {showEditModal && editingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Edit Transaction</h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <EditTransactionForm
                transaction={editingTransaction}
                onSubmit={handleUpdateTransaction}
                onDelete={handleDeleteTransaction}
                onCancel={handleCloseEditModal}
                isSubmitting={isSubmittingTransaction}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// History Tab Component with Lazy Loading
function HistoryTab({
  userTransactions,
  hasMoreTransactions,
  isLoadingMore,
  loadMoreTransactions,
  formatDate,
  handleEditTransaction,
  handleRefresh,
  isRefreshing
}) {
  const containerRef = React.useRef(null);

  // Scroll detection for lazy loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when user scrolls to within 200px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 200 && !isLoadingMore && hasMoreTransactions) {
        loadMoreTransactions();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreTransactions, isLoadingMore, loadMoreTransactions]);

  return (
    <PullToRefresh onRefresh={handleRefresh} isRefreshing={isRefreshing}>
      <div className="space-y-8" ref={containerRef}>
        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Transaction History</h2>
            <p className="mt-1 text-sm text-gray-500">
              All your submitted transactions
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {userTransactions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Plus className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions yet</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first transaction on the Home tab.</p>
              </div>
            ) : (
              <>
                {userTransactions.map((transaction) => (
                  <div key={transaction.id} className="p-6 flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <DollarSign className="h-4 w-4 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-sm font-medium text-gray-900">
                          {transaction.vendor}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {formatAmount(transaction.amount)}
                          {transaction.category && ` • ${transaction.category}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {transaction.statementTransaction?.cardNumber && (
                            <p className="text-xs text-gray-500">
                              Card •••• {transaction.statementTransaction.cardNumber}
                            </p>
                          )}
                          {transaction.isMatched && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Matched
                            </span>
                          )}
                        </div>
                        {transaction.notes && (
                          <p className="text-xs text-gray-400 mt-1">{transaction.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(transaction.transactionDate)}
                        </div>
                      </div>
                      {!transaction.statementTransaction?.statementGroup?.processed && (
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
                          title="Edit transaction"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoadingMore && (
                  <div className="p-6 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="h-5 w-5 text-primary-600 animate-spin" />
                      <span className="text-sm text-gray-600">Loading more transactions...</span>
                    </div>
                  </div>
                )}

                {/* No more transactions indicator */}
                {!hasMoreTransactions && userTransactions.length > 0 && (
                  <div className="p-6 text-center">
                    <p className="text-sm text-gray-500">No more transactions to load</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </PullToRefresh>
  );
}

// Edit Transaction Form Component
function EditTransactionForm({ transaction, onSubmit, onDelete, onCancel, isSubmitting }) {
  const [formData, setFormData] = useState({
    transactionDate: transaction.transactionDate,
    amount: transaction.amount.toString(),
    vendor: transaction.vendor,
    category: transaction.category || '',
    notes: transaction.notes || '',
    receiptImage: null,
    removeImage: false
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [hasExistingImage, setHasExistingImage] = useState(!!transaction.receiptImagePath);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const categories = [
    'Food & Dining',
    'Gas & Fuel',
    'Groceries',
    'Shopping',
    'Entertainment',
    'Travel',
    'Medical',
    'Office Supplies',
    'Restaurant Supplies',
    'Utilities',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        receiptImage: file,
        removeImage: false
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setHasExistingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      receiptImage: null,
      removeImage: true
    }));
    setImagePreview(null);
    setHasExistingImage(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.transactionDate) {
      newErrors.transactionDate = 'Transaction date is required';
    }

    if (formData.amount === '' || isNaN(formData.amount) || parseFloat(formData.amount) === 0) {
      newErrors.amount = 'Please enter a valid amount (use a negative amount for returns)';
    }

    if (!formData.vendor.trim()) {
      newErrors.vendor = 'Vendor name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.notes.trim()) {
      newErrors.notes = 'Notes are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const transactionData = {
      ...formData,
      amount: parseFloat(formData.amount)
    };

    await onSubmit(transactionData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Transaction Date */}
        <div>
          <label htmlFor="transactionDate" className="block text-sm font-medium text-gray-700 mb-1">
            Transaction Date
          </label>
          <input
            type="date"
            id="transactionDate"
            name="transactionDate"
            value={formData.transactionDate}
            onChange={handleChange}
            className={`block w-full px-3 py-2 border ${errors.transactionDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
            required
          />
          {errors.transactionDate && (
            <p className="mt-1 text-sm text-red-600">{errors.transactionDate}</p>
          )}
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Amount
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              id="amount"
              name="amount"
              step="0.01"
              value={formData.amount}
              onChange={handleChange}
              placeholder="0.00"
              className={`block w-full pl-7 pr-3 py-2 border ${errors.amount ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
              required
            />
          </div>
          {errors.amount ? (
            <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">For a return or refund, enter a negative amount (e.g. -6.47).</p>
          )}
        </div>
      </div>

      {/* Vendor */}
      <div>
        <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-1">
          Vendor/Merchant
        </label>
        <input
          type="text"
          id="vendor"
          name="vendor"
          value={formData.vendor}
          onChange={handleChange}
          placeholder="e.g., Starbucks, Shell Gas Station, Amazon"
          className={`block w-full px-3 py-2 border ${errors.vendor ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
          required
        />
        {errors.vendor && (
          <p className="mt-1 text-sm text-red-600">{errors.vendor}</p>
        )}
      </div>

      {/* Category */}
      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          className={`block w-full px-3 py-2 border ${errors.category ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
          required
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          value={formData.notes}
          onChange={handleChange}
          placeholder="Additional details about this transaction..."
          className={`block w-full px-3 py-2 border ${errors.notes ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
          required
        />
        {errors.notes && (
          <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
        )}
      </div>

      {/* Receipt Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Receipt Image
        </label>

        {/* Existing Image */}
        {hasExistingImage && !imagePreview && (
          <div className="mb-3 p-4 border border-gray-300 rounded-md bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">Current receipt image attached</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setShowReceiptModal(true)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center space-x-1"
                >
                  <ImageIcon className="h-4 w-4" />
                  <span>View</span>
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center space-x-1"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Remove</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* New Image Preview */}
        {imagePreview && (
          <div className="mb-3 p-4 border border-gray-300 rounded-md bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">New receipt image</span>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center space-x-1"
              >
                <Trash2 className="h-4 w-4" />
                <span>Remove</span>
              </button>
            </div>
            <div className="max-h-96 flex items-center justify-center">
              <ImageZoom
                src={imagePreview}
                alt="Receipt preview"
                className="max-h-96"
              />
            </div>
          </div>
        )}

        {/* Upload Button */}
        {!imagePreview && (
          <div className="flex items-center space-x-2">
            <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
              <Upload className="h-4 w-4" />
              <span>{hasExistingImage ? 'Replace Image' : 'Upload Image'}</span>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
            <span className="text-xs text-gray-500">Images and PDFs accepted (max 10MB)</span>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => onDelete(transaction.id)}
          disabled={isSubmitting || transaction.statementTransaction?.statementGroup?.processed}
          className="px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          title={transaction.statementTransaction?.statementGroup?.processed ? 'Cannot delete processed transactions' : 'Delete transaction'}
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete</span>
        </button>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Receipt Viewing Modal */}
      {showReceiptModal && transaction.receiptImagePath && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4" onClick={() => setShowReceiptModal(false)}>
          <div className="relative max-w-5xl w-full bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {transaction.receiptImagePath.toLowerCase().endsWith('.pdf') ? 'Receipt PDF' : 'Receipt Image'}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowReceiptModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex justify-center">
                {transaction.receiptImagePath.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={`/api/user-transactions/receipt-image/${transaction.receiptImagePath.split('/').pop()}`}
                    title="Receipt PDF"
                    className="w-full h-[70vh] rounded-lg"
                  />
                ) : (
                  <ImageZoom
                    src={`/api/user-transactions/receipt-image/${transaction.receiptImagePath.split('/').pop()}`}
                    alt="Receipt"
                    className="w-full min-h-[70vh] flex items-center justify-center"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// Wrapper component to handle routing
function AppWrapper() {
  return (
    <Routes>
      <Route path="/" element={<App />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
    </Routes>
  );
}

export default AppWrapper;
